import React, { useState, useRef, useEffect } from 'react';
import './RichTextEditor.css';

const RichTextEditor = ({ value, onChange, placeholder = 'Enter content...' }) => {
  const [showHTMLSource, setShowHTMLSource] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const editorRef = useRef(null);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isUpdatingRef.current && editorRef.current.innerHTML !== value) {
      const selection = window.getSelection();
      const hadFocus = document.activeElement === editorRef.current;
      editorRef.current.innerHTML = value || '';
      if (hadFocus && selection.rangeCount > 0) {
        editorRef.current.focus();
      }
    }
    if (showHTMLSource) {
      setDraftValue(value || '');
    }
  }, [value, showHTMLSource]);

  const handleContentChange = () => {
    if (editorRef.current) {
      isUpdatingRef.current = true;
      onChange(editorRef.current.innerHTML);
      setTimeout(() => { isUpdatingRef.current = false; }, 10);
    }
  };

  const handleHTMLChange = (e) => {
    const nextValue = e.target.value;
    setDraftValue(nextValue);
    onChange(nextValue);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    handleContentChange();
  };

  const insertHTML = () => {
    const html = prompt('Enter HTML code (e.g., embed code):');
    if (html) {
      document.execCommand('insertHTML', false, html);
      handleContentChange();
    }
  };

  const handleToggleHtmlSource = () => {
    if (showHTMLSource) {
      const nextValue = draftValue || value || '';
      if (editorRef.current) editorRef.current.innerHTML = nextValue;
      onChange(nextValue);
      setShowHTMLSource(false);
      return;
    }
    if (editorRef.current) {
      const currentHtml = editorRef.current.innerHTML || '';
      setDraftValue(currentHtml);
      onChange(currentHtml);
    } else {
      setDraftValue(value || '');
    }
    setShowHTMLSource(true);
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <button type="button" className="toolbar-btn" onClick={() => execCommand('bold')} title="Bold">
            <strong>B</strong>
          </button>
          <button type="button" className="toolbar-btn" onClick={() => execCommand('italic')} title="Italic">
            <em>I</em>
          </button>
          <button type="button" className="toolbar-btn" onClick={() => execCommand('underline')} title="Underline">
            <u>U</u>
          </button>
        </div>

        <div className="toolbar-group">
          <button type="button" className="toolbar-btn" onClick={() => execCommand('formatBlock', '<h2>')} title="Heading 2">H2</button>
          <button type="button" className="toolbar-btn" onClick={() => execCommand('formatBlock', '<h3>')} title="Heading 3">H3</button>
          <button type="button" className="toolbar-btn" onClick={() => execCommand('formatBlock', '<p>')} title="Paragraph">P</button>
        </div>

        <div className="toolbar-group">
          <button type="button" className="toolbar-btn" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">•</button>
          <button type="button" className="toolbar-btn" onClick={() => execCommand('insertOrderedList')} title="Numbered List">1.</button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => { const url = prompt('Enter URL:'); if (url) execCommand('createLink', url); }}
            title="Insert Link"
          >
            🔗
          </button>
          <button type="button" className="toolbar-btn" onClick={insertHTML} title="Insert HTML">
            &lt;/&gt;
          </button>
          <button
            type="button"
            className={`toolbar-btn ${showHTMLSource ? 'active' : ''}`}
            onClick={handleToggleHtmlSource}
            title="Toggle HTML Source"
          >
            📝
          </button>
        </div>
      </div>

      {showHTMLSource ? (
        <textarea
          className="html-source-editor"
          value={draftValue}
          onChange={handleHTMLChange}
          placeholder="Edit HTML source..."
          rows={15}
        />
      ) : (
        <div
          ref={editorRef}
          className="editor-content"
          contentEditable
          onInput={handleContentChange}
          suppressContentEditableWarning
          data-placeholder={placeholder}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
